/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { TextField, Typography2, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { Select } from '@coursera/coursera-ui';

import type {
  FilterOption,
  MultiSelectState,
} from 'bundles/enterprise-admin-coursera-for-campus-basic/components/CourseraForCampusBasicSetupContainer';

import _t from 'i18n!nls/enterprise-admin-coursera-for-campus-basic';

const rcProgramSurvey = (theme: Theme) =>
  css({
    background: 'var(--cds-color-blue-50)',
    borderRadius: '4px',
    padding: 'var(--cds-spacing-300)',
    margin: 'var(--cds-spacing-200) 0 var(--cds-spacing-400) 0',
    fontSize: '16px',
    lineHeight: '30px',
  });

const surveyHeader = css({
  display: 'flex',
  justifyContent: 'space-between',
});

const surveyTitle = css({
  '&&': {
    fontSize: '14px',
  },
});

const surveyValidationText = (theme: Theme) =>
  css({
    '&&': {
      color: 'var(--cds-color-red-700)',
      fontSize: '12px',
      fontWeight: 'bold',
    },
  });

const surveyDescription = (theme: Theme) =>
  css({
    '&&': {
      color: 'var(--cds-color-grey-600)',
    },
    marginBottom: 'var(--cds-spacing-150)',
  });

const selectLabel = css({
  '&&': {
    textTransform: 'uppercase',
    fontSize: '12px',
  },
});

const programUsecaseSection = css({
  display: 'flex',
  justifyContent: 'space-between',
});

const programUsecaseColumn = css({
  width: '49%',
});

const prefix = css({
  '&&': {
    fontWeight: 'bold',
    display: 'inline',
  },
});

const programUsecaseOption = ({ isSectionComplete, theme }: { isSectionComplete: boolean; theme: Theme }) =>
  css({
    display: 'flex',
    background: '#fff',
    padding: 'var(--cds-spacing-150)',
    marginBottom: 'var(--cds-spacing-150)',
    alignItems: 'flex-start',
    border: isSectionComplete ? `1px solid var(--cds-color-grey-25)` : `1px solid var(--cds-color-red-700)`,
    borderRadius: '4px',
  });

const programUsecaseLabel = (theme: Theme) =>
  css({
    fontWeight: 'normal',
    marginLeft: 'var(--cds-spacing-150)',
    lineHeight: '20px',
    fontSize: '14px',
  });

export type Props = {
  selectedProgramUsecases: Array<string>;
  selectedDepartments: MultiSelectState;
  departmentText: string;
  programInfoText: string;
  onDepartmentChange: (option: Array<FilterOption>) => void;
  onDepartmentTextChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProgramUsecaseChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProgramInfoTextChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isRetro?: boolean;
};

const ProgramSurvey = ({
  departmentText,
  programInfoText,
  selectedProgramUsecases,
  selectedDepartments,
  onDepartmentChange,
  onDepartmentTextChange,
  onProgramInfoTextChange,
  onProgramUsecaseChange,
  isRetro,
}: Props) => {
  const getProgramUsecaseOptions = () => [
    {
      value: 'For credit',
      label: (
        <FormattedMessage
          message={_t(
            '{prefix} students complete Coursera course on their own to gain standalone education institution credit'
          )}
          prefix={
            <Typography2 component="p" variant="bodySecondary" css={prefix}>
              {_t('For credit:')}
            </Typography2>
          }
        />
      ),
    },
    {
      value: 'For credit - blended learning (courses)',
      label: (
        <FormattedMessage
          message={_t(
            '{prefix} students complete Coursera course as part of an on campus course and receive some credit (e.g. offered as an assignment as a portion of the overall grade)'
          )}
          prefix={
            <Typography2 component="p" variant="bodySecondary" css={prefix}>
              {_t('For credit - blended learning (courses):')}
            </Typography2>
          }
        />
      ),
    },
    {
      value: 'For credit - blended learning (guided project)',
      label: (
        <FormattedMessage
          message={_t(
            '{prefix} students complete Coursera guided project as part of an on campus course and receive some credit (e.g. offered as an assignment as a portion of the overall grade)'
          )}
          prefix={
            <Typography2 component="p" variant="bodySecondary" css={prefix}>
              {_t('For credit - blended learning (guided project):')}
            </Typography2>
          }
        />
      ),
    },
    {
      value: 'Not for credit - part of a university course',
      label: (
        <FormattedMessage
          message={_t(
            '{prefix} - students have access to Coursera content as part of a education institution offered course but receive no credit (e.g. as supplemental material)'
          )}
          prefix={
            <Typography2 component="p" variant="bodySecondary" css={prefix}>
              {_t('Not for credit')}
            </Typography2>
          }
        />
      ),
    },
    {
      value: 'Not for credit - students choose',
      label: (
        <FormattedMessage
          message={_t(
            '{prefix} - students have access to Coursera content to choose whichever course they want to take (not directly related to on campus courses)'
          )}
          prefix={
            <Typography2 component="p" variant="bodySecondary" css={prefix}>
              {_t('Not for credit')}
            </Typography2>
          }
        />
      ),
    },
    {
      value: 'Faculty training',
      label: (
        <FormattedMessage
          message={_t('{prefix} - offered to education institution faculty for their ongoing training/development')}
          prefix={
            <Typography2 component="p" variant="bodySecondary" css={prefix}>
              {_t('Faculty training')}
            </Typography2>
          }
        />
      ),
    },
    {
      value: 'Other',
      label: _t('Other'),
    },
  ];

  const getDepartmentOptions = () => [
    {
      value: 'All departments',
      label: <Typography2 component="p">{_t('All departments')}</Typography2>,
    },
    {
      value: 'Business',
      label: <Typography2 component="p">{_t('Business')}</Typography2>,
    },
    {
      value: 'Computer science',
      label: <Typography2 component="p">{_t('Computer Science')}</Typography2>,
    },
    {
      value: 'Engineering',
      label: <Typography2 component="p">{_t('Engineering')}</Typography2>,
    },
    {
      value: 'Healthcare & Life Sciences',
      label: <Typography2 component="p">{_t('Healthcare & Life Sciences')}</Typography2>,
    },
    {
      value: 'Law',
      label: <Typography2 component="p">{_t('Law')}</Typography2>,
    },
    {
      value: 'Liberal Arts & Humanities',
      label: <Typography2 component="p">{_t('Liberal Arts & Humanities')}</Typography2>,
    },
    {
      value: 'Mathematics',
      label: <Typography2 component="p">{_t('Mathematics')}</Typography2>,
    },
    {
      value: 'Sciences',
      label: <Typography2 component="p">{_t('Sciences')}</Typography2>,
    },
    {
      value: 'Social Sciences',
      label: <Typography2 component="p">{_t('Social Sciences')}</Typography2>,
    },
    {
      value: 'Other',
      label: <Typography2 component="p">{_t('Other')}</Typography2>,
    },
  ];

  const getSurveyQuestions = () => ({
    program: isRetro
      ? _t('How did you use this learning program? Was it used for credit?')
      : _t('How do you plan to use this learning program? Will it be used for credit?'),
    department: isRetro
      ? _t('What department(s) was this content for?')
      : _t('What department(s) is this content for?'),
  });

  const shouldRenderDepartmentTextInput = selectedDepartments.multiValue.some((option) => option.value === 'Other');
  const shouldRenderProgramTextInput = selectedProgramUsecases.some((option) => option === 'Other');
  const isSurveyIncomplete =
    !selectedProgramUsecases.length ||
    !selectedDepartments.multiValue.length ||
    (shouldRenderProgramTextInput && !programInfoText) ||
    (shouldRenderDepartmentTextInput && !departmentText);
  const isDepartmentSelected = selectedDepartments.multiValue.length > 0;

  const theme = useTheme();

  return (
    <div css={rcProgramSurvey(theme)} id="InviteScreenProgramSurvey">
      <div css={surveyHeader}>
        <Typography2 component="h3" variant="subtitleMedium" css={surveyTitle}>
          {_t('We’d love to learn a little more')}
        </Typography2>
        {isSurveyIncomplete && (
          <Typography2 component="p" variant="bodySecondary" css={surveyValidationText(theme)}>
            {_t('*This section is required')}
          </Typography2>
        )}
      </div>
      <Typography2 component="p" variant="bodySecondary" css={surveyDescription(theme)}>
        {_t(
          'Tell us more about your goals for this learning program. Your selections will help us better understand how to serve your needs; they will not be shown to learners.'
        )}
      </Typography2>
      <form>
        <Typography2 component="h3" variant="subtitleMedium" css={selectLabel}>
          {getSurveyQuestions().program}
        </Typography2>
        <div css={programUsecaseSection}>
          <fieldset css={programUsecaseColumn}>
            {getProgramUsecaseOptions()
              .slice(0, 3)
              .map((option) => (
                <div
                  key={option.value}
                  css={programUsecaseOption({ isSectionComplete: selectedProgramUsecases.length > 0, theme })}
                >
                  <input
                    id={option.value}
                    type="checkbox"
                    onChange={onProgramUsecaseChange}
                    value={option.value}
                    checked={selectedProgramUsecases.includes(option.value)}
                  />
                  <Typography2 component="p" variant="bodySecondary">
                    <label htmlFor={option.value} css={programUsecaseLabel(theme)}>
                      {option.label}
                    </label>
                  </Typography2>
                </div>
              ))}
          </fieldset>
          <fieldset css={programUsecaseColumn}>
            {getProgramUsecaseOptions()
              .slice(3)
              .map((option) => (
                <div
                  key={option.value}
                  css={programUsecaseOption({ isSectionComplete: selectedProgramUsecases.length > 0, theme })}
                >
                  <input
                    id={option.value}
                    type="checkbox"
                    onChange={onProgramUsecaseChange}
                    value={option.value}
                    checked={selectedProgramUsecases.includes(option.value)}
                  />
                  <Typography2 component="p" variant="bodySecondary">
                    <label htmlFor={option.value} css={programUsecaseLabel(theme)}>
                      {option.label}
                    </label>
                  </Typography2>
                </div>
              ))}
          </fieldset>
        </div>
        {shouldRenderProgramTextInput && (
          <TextField
            multiline
            fullWidth
            value={programInfoText}
            onChange={onProgramInfoTextChange}
            placeholder={_t('Tell us more')}
            validationStatus={!programInfoText ? 'error' : 'success'}
            label={_t('')}
          />
        )}
        <fieldset>
          <Typography2 component="h3" variant="subtitleMedium" css={selectLabel} style={{ marginTop: '12px' }}>
            {getSurveyQuestions().department}
          </Typography2>
          <Select
            options={getDepartmentOptions()}
            value={selectedDepartments.multiValue}
            onChange={onDepartmentChange}
            multi
            htmlAttributes={{
              style: {
                borderColor: isDepartmentSelected ? '#ccc' : '#D20001',
              },
            }}
          />
          {shouldRenderDepartmentTextInput && (
            <TextField
              multiline
              fullWidth
              value={departmentText}
              onChange={onDepartmentTextChange}
              placeholder={_t('What other department(s) is this content for?')}
              validationStatus={!departmentText ? 'error' : 'success'}
              label={_t('')}
            />
          )}
        </fieldset>
      </form>
    </div>
  );
};

export default ProgramSurvey;
